'use client';

import { UtilityCompanyOther } from '@/lib/actions/usage/constants';
import {
    baseUsageAtom,
    basicInfoExpandAtom,
    electricHeatAtom,
    flexAtom,
    hasAcAtom,
    hasElectricStoveAtom,
    hasElectricWaterHeaterAtom,
    hasHeatPumpAtom,
    hhSizeAtom,
    sqftAtom,
    storiesAtom,
    utilityCoAtom,
    yearBuiltAtom,
    zipCodeAtom,
} from '@/ui/atoms/form-atoms';
import { Loader, Select, Text, TextInput, Transition } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import clsx from 'clsx';
import { useAtom } from 'jotai';
import { useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import { AccessibleTooltip, asteriskTooltipTarget } from '../AccessibleTooltip';
import styles from './BasicInfoMenu.module.css';
import {
    BooleanInput,
    InputStyleClassName,
    NumberInput,
    Row,
} from './building-blocks';
const UtilityCompanyEmpty = '';

const scaleY = {
    in: { opacity: 1, transform: 'scaleY(1)' },
    out: { opacity: 0, transform: 'scaleY(0)' },
    common: { transformOrigin: 'top' },
    transitionProperty: 'transform, opacity',
};

// Fetcher function for useSWR
const fetcher = (url: string) => fetch(url).then((res) => res.json());

// Section component for collapsible sections
const Section: React.FC<{
    title: string;
    children: React.ReactNode;
    defaultExpanded?: boolean;
}> = ({ title, children, defaultExpanded = true }) => {
    const [expanded, setExpanded] = useState(defaultExpanded);

    return (
        <div className={styles.section}>
            <div
                className={styles.sectionHeader}
                onClick={() => setExpanded((prev) => !prev)}
            >
                {title}{' '}
                <IconChevronDown
                    style={{
                        transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.25s ease',
                    }}
                />
            </div>
            <hr
                style={{
                    width: '100%',
                    border: '0.5px solid #e5e5e5',
                    marginTop: '8px',
                    marginBottom: '8px',
                }}
            />
            {expanded && (
                <div className={styles.sectionContent}>{children}</div>
            )}
        </div>
    );
};

export const BasicInfoMenu: React.FC = () => {
    const [expand, setExpand] = useAtom(basicInfoExpandAtom);

    const [flex, setFlex] = useAtom(flexAtom);
    const [baseUsage, setBaseUsage] = useAtom(baseUsageAtom);
    const [zipCode, setZipCode] = useAtom(zipCodeAtom);
    const [yearBuilt, setYearBuilt] = useAtom(yearBuiltAtom);
    const [sqft, setSqft] = useAtom(sqftAtom);
    const [hhSize, setHhSize] = useAtom(hhSizeAtom);
    const [stories, setStories] = useAtom(storiesAtom);
    const [utilityCo, setUtilityCo] = useAtom(utilityCoAtom);

    const [hasAc, setHasAc] = useAtom(hasAcAtom);
    const [electricHeat, setElectricHeat] = useAtom(electricHeatAtom);
    const [hasHeatPump, setHasHeatPump] = useAtom(hasHeatPumpAtom);
    const [hasElectricStove, setHasElectricStove] =
        useAtom(hasElectricStoveAtom);
    const [hasElectricWaterHeater, setHasElectricWaterHeater] = useAtom(
        hasElectricWaterHeaterAtom
    );

    const [selectableUtilityCompanies, setSelectableUtilityCompanies] =
        useState<string[]>();

    // Fetch zipcode data using useSWR when zipCode changes
    const {
        data: zipcodeData,
        error: zipcodeError,
        isLoading: isLoadingUtilities,
    } = useSWR(
        zipCode && zipCode.length === 5
            ? `/api/zipcode-utilities?zipcode=${zipCode}`
            : null,
        fetcher
    );

    // Extract state from zipcode API response
    const state = useMemo(() => {
        if (
            !zipCode ||
            !zipcodeData ||
            !zipcodeData.data ||
            zipcodeData.data.length === 0
        )
            return '';

        // Get state from the first utility data entry
        return zipcodeData.data[0]?.state || '';
    }, [zipCode, zipcodeData]);

    useEffect(() => {
        if (!zipCode || !zipcodeData || zipCode.length < 5) {
            setSelectableUtilityCompanies(undefined);
            return;
        }

        if (!zipcodeData.data || zipcodeData.data.length === 0) {
            setSelectableUtilityCompanies([UtilityCompanyOther]);
            return;
        }

        // Extract utility companies from the zipcode data
        const utilities = zipcodeData.data.map(
            (item: { utility_name: string }) => item.utility_name
        );

        // Always add empty string '' to allow the user to unselect.
        // Always add 'Other' in case our data is outdated/incomplete.
        const dropdownOptions = [UtilityCompanyEmpty, ...utilities];

        if (utilities.length === 0) {
            dropdownOptions.push(UtilityCompanyOther);
        }

        setSelectableUtilityCompanies(dropdownOptions);

        // Reset utilityCo if it is not found in the new list.
        if (
            !utilities.find((u: string) => u === utilityCo) &&
            utilityCo !== UtilityCompanyOther
        ) {
            setUtilityCo(UtilityCompanyEmpty);
        }

        // if 'Other' was selected, and we have other options available, then
        // reset the selection.
        else if (
            dropdownOptions.length > 1 &&
            utilityCo === UtilityCompanyOther
        ) {
            setUtilityCo(UtilityCompanyEmpty);
        }
    }, [setUtilityCo, zipCode, zipcodeData, utilityCo]);

    useEffect(() => {
        if (!electricHeat) {
            setHasHeatPump(false);
        }
    }, [electricHeat, setHasHeatPump]);

    // Basic customer info section
    const customerInfoSection = (
        <>
            <div className={styles.companyName}>Sunrun Flex</div>
            <hr
                style={{
                    width: '100%',
                    border: '0.5px solid #e5e5e5',
                    marginTop: '8px',
                    marginBottom: '8px',
                }}
            />
            <BooleanInput
                label={
                    <span className={styles.flexLabel}>
                        Flex Customer
                        <AccessibleTooltip
                            label={
                                <>
                                    As a good candidate for Sunrun Flex, we will
                                    <br />
                                    optimize your solar design for your roof
                                    size
                                    <br />
                                    and not your pre-solar usage.
                                </>
                            }
                        >
                            {asteriskTooltipTarget}
                        </AccessibleTooltip>
                    </span>
                }
                value={flex}
                setValue={setFlex}
                className={styles.switch}
            />
        </>
    );

    // Home information section
    const homeInfoSection = (
        <>
            <Row>
                <TextInput
                    label="Zip Code"
                    value={zipCode}
                    onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '');
                        setZipCode(value);
                        setUtilityCo(UtilityCompanyEmpty);
                    }}
                    maxLength={5}
                    inputMode="numeric"
                    pattern="[0-9]*"
                />

                <Select
                    key={state + expand}
                    className={InputStyleClassName}
                    checkIconPosition="right"
                    label="Utility Company"
                    value={utilityCo}
                    onChange={(value) => {
                        setUtilityCo(
                            !value || value === UtilityCompanyEmpty ? '' : value
                        );
                    }}
                    data={selectableUtilityCompanies}
                    placeholder={
                        selectableUtilityCompanies === undefined
                            ? 'Enter zip code first'
                            : 'Pick a utility'
                    }
                    rightSection={
                        isLoadingUtilities &&
                        zipCode &&
                        zipCode.length === 5 ? (
                            <Loader size="xs" />
                        ) : (
                            <IconChevronDown size={16} />
                        )
                    }
                    disabled={isLoadingUtilities}
                />
            </Row>

            <Row>
                <NumberInput
                    label="Square Footage"
                    value={sqft}
                    setValue={(v) => setSqft(v)}
                />
                <NumberInput
                    label="Year Built"
                    value={yearBuilt}
                    setValue={(v) => setYearBuilt(v)}
                />
            </Row>

            <Row>
                <NumberInput
                    label="Number of Stories"
                    value={stories}
                    setValue={(v) => setStories(v)}
                />

                <NumberInput
                    label="Number of Occupants"
                    value={hhSize}
                    setValue={(v) => setHhSize(v)}
                />
            </Row>
        </>
    );

    // Energy usage section
    const energyUsageSection = (
        <>
            <NumberInput
                label="Annual Energy Usage (kWh)"
                value={baseUsage}
                setValue={(v) => setBaseUsage(v)}
            />

            <Text>Currently Have:</Text>

            <BooleanInput
                label="Air Conditioner"
                value={hasAc}
                setValue={(v) => setHasAc(v)}
            />

            <BooleanInput
                label="Electric Heat"
                value={electricHeat}
                setValue={(v) => setElectricHeat(v)}
            />

            {electricHeat && (
                <BooleanInput
                    label="Heat Pump"
                    value={hasHeatPump}
                    setValue={(v) => setHasHeatPump(v)}
                />
            )}

            <BooleanInput
                label="Electric Stove"
                value={hasElectricStove}
                setValue={(v) => setHasElectricStove(v)}
            />

            <BooleanInput
                label="Electric Water Heater"
                value={hasElectricWaterHeater}
                setValue={(v) => setHasElectricWaterHeater(v)}
            />
        </>
    );

    return (
        <div className={styles.menuContainer}>
            <div
                className={clsx(styles.menuTarget, expand && styles.active)}
                onClick={() => setExpand((v) => !v)}
            >
                Enter In Your Details{' '}
                <IconChevronDown
                    style={{
                        transform: expand ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.25s ease',
                    }}
                />
            </div>

            <Transition
                mounted={expand}
                transition={scaleY}
                duration={250}
                timingFunction="ease"
                keepMounted
            >
                {(transitionStyle) => (
                    <>
                        <div
                            className={clsx(
                                styles.overlay,
                                expand && styles.active
                            )}
                            onClick={() => {
                                if (expand) {
                                    setExpand((v) => !v);
                                }
                            }}
                        />
                        <div
                            className={styles.dropdown}
                            style={transitionStyle}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div>{customerInfoSection}</div>
                            <Section title="Home Information">
                                {homeInfoSection}
                            </Section>

                            <Section title="Energy Usage Information">
                                {energyUsageSection}
                            </Section>
                        </div>
                    </>
                )}
            </Transition>
        </div>
    );
};
